// Economics System for Canntycoon
import { EXTRACT_CONFIGS } from '../data/extractions';
import { getStrain } from '../data/strains';

// Extract pricing now uses market prices: extractRosin (100-200), extractWax (20-40), extractShatter (10-60)
// priceMultiplier field in EXTRACT_CONFIGS is preserved for potential yield calculations but no longer used for pricing

// ── Quality Tier Definitions ──────────────────────────────────────────────────
// Each tier has a label, color, emoji, and price multiplier.
// 75+ = Premium bonus, 85+ = Exotic (large payout), 90+ = Legendary.
export function getQualityTier(qualityScore) {
  if (qualityScore >= 90) return { label: 'Legendary', color: '#ff6b35', emoji: '🏆', mult: 2.8 };
  if (qualityScore >= 85) return { label: 'Exotic',    color: '#b347ff', emoji: '💎', mult: 2.2 };
  if (qualityScore >= 75) return { label: 'Premium',   color: '#3fb950', emoji: '⭐', mult: 1.65 };
  if (qualityScore >= 65) return { label: 'Good',      color: '#58a6ff', emoji: '✓',  mult: 1.3  };
  if (qualityScore >= 55) return { label: 'Average',   color: '#d29922', emoji: '',   mult: 1.0  };
  if (qualityScore >= 40) return { label: 'Below Avg', color: '#d29922', emoji: '',   mult: 0.80 };
  if (qualityScore >= 25) return { label: 'Poor',      color: '#f85149', emoji: '',   mult: 0.65 };
  return                         { label: 'Junk',      color: '#6e7681', emoji: '',   mult: 0.5  };
}

// ── Quality → Price Multiplier ────────────────────────────────────────────────
export function getQualityMultiplier(qualityScore) {
  return getQualityTier(qualityScore).mult;
}

// ── Harvest Value Calculation ─────────────────────────────────────────────────
export function calculateHarvestValue(inventoryItem, channel, reputation, marketPrices, supplyGlut, unlockedTech) {
  const itemType = inventoryItem.type ?? 'flower';
  const tier     = getQualityTier(inventoryItem.qualityScore);
  const repBonus = 1 + (reputation / 100) * 0.25;

  // ── Extract pricing path ───────────────────────────────────────────────────
  if (itemType === 'extract') {
    const medicalThcBonus = (channel === 'medical' && (inventoryItem.thcAccumulation ?? 0) >= 30) ? 1.35 : 1.0;
    const styleKey = inventoryItem.extractType && EXTRACT_CONFIGS[inventoryItem.extractType]
      ? inventoryItem.extractType
      : 'rosin';
    const marketKey = `extract${styleKey.charAt(0).toUpperCase() + styleKey.slice(1)}`;
    const basePrice = marketPrices[marketKey] ?? 150;
    const pricePerGram = basePrice * tier.mult * repBonus * medicalThcBonus;
    return {
      pricePerGram:    Math.round(pricePerGram * 100) / 100,
      totalValue:      Math.round(pricePerGram * inventoryItem.grams),
      tier,
      baseChannelPrice: Math.round(basePrice * 100) / 100,
    };
  }

  // ── Flower / packaged pricing ──────────────────────────────────────────────
  const strain = getStrain(inventoryItem.strainId);
  if (!strain) return { pricePerGram: 0, totalValue: 0, tier: getQualityTier(0), baseChannelPrice: 0 };

  const strainPriceMult    = strain?.marketBasePrice ? (strain.marketBasePrice / 3.50) : 1.0;
  const channelBasePrice   = (marketPrices[channel] || marketPrices.wholesale) * strainPriceMult;
  const qualityMult        = tier.mult;
  const strainChannelAppeal = strain.channelAppeal[channel] || 0.7;
  const gutPenalty         = supplyGlut ? 0.80 : 1.0;
  const contractBonus      = unlockedTech?.includes('D3') ? 1.15 : 1.0;
  const isCBDStrain        = strain.cbdCeiling && strain.cbdCeiling > strain.thcCeiling;
  const cbdMedicalBonus    = (isCBDStrain && channel === 'medical') ? 1.35 : 1.0;
  const pkgTier = inventoryItem.packageTier ?? (inventoryItem.packaged ? 1 : 0);
  const PACKAGE_MULTS = [
    { retail: 1.0,  medical: 1.0,  wholesale: 1.0  },  // tier 0: bulk
    { retail: 1.15, medical: 1.10, wholesale: 1.0  },  // tier 1: standard
    { retail: 1.30, medical: 1.20, wholesale: 1.05 },  // tier 2: branded
    { retail: 1.50, medical: 1.35, wholesale: 1.10 },  // tier 3: premium
  ];
  const packagedBonus = (PACKAGE_MULTS[pkgTier] || PACKAGE_MULTS[0])[channel] || 1.0;
  const curedMultiplier    = inventoryItem.status === 'cured'
    ? 1.0 + Math.min(inventoryItem.cureBonus ?? 0, 20) / 100
    : 1.0;

  const pricePerGram = channelBasePrice
    * qualityMult
    * strainChannelAppeal
    * repBonus
    * gutPenalty
    * contractBonus
    * cbdMedicalBonus
    * packagedBonus
    * curedMultiplier;

  return {
    pricePerGram:    Math.round(pricePerGram * 100) / 100,
    totalValue:      Math.round(pricePerGram * inventoryItem.grams),
    tier,
    baseChannelPrice: Math.round(channelBasePrice * strainChannelAppeal * repBonus * gutPenalty * contractBonus * 100) / 100,
  };
}

// ── Review Score Calculation ──────────────────────────────────────────────────
// inventoryItem must have qualityScore, thcAccumulation, terpeneProfile fields
export function calculateReviewScore(inventoryItem, reviewer, illegalSalesCount = 0) {
  const base = inventoryItem.qualityScore || 50;
  let score = base;

  switch (reviewer.bias) {
    case 'terpene':
      // Terpene profile matters 60%, overall quality 40%
      score = (inventoryItem.terpeneProfile || base) * 0.6 + base * 0.4;
      break;
    case 'potency':
      // THC accumulation matters 60%, overall quality 40%
      score = (inventoryItem.thcAccumulation || base) * 0.6 + base * 0.4;
      break;
    case 'compliance':
      // Penalise heavily for each illegal sale on record
      score = base - Math.min(25, illegalSalesCount * 4);
      break;
    case 'medical':
      // Balanced but penalises low visual appeal (consistency signal)
      score = base * 0.7 + (inventoryItem.visualAppeal || base) * 0.3;
      break;
    case 'value':
      // Quality relative to grams delivered — generous batches get a bump
      const gramsBonus = inventoryItem.grams >= 200 ? 5 : inventoryItem.grams >= 100 ? 2 : 0;
      score = base + gramsBonus;
      break;
    case 'balanced':
    default:
      score = base;
      break;
  }

  // Corporate brand bias — indie critics penalise corporate-backed ops, mainstream favour them
  if (inventoryItem.corporateBacked) score += reviewer.corporateBias || 0;

  // Random critic variance ±8
  score += (Math.random() - 0.5) * 16;
  return Math.round(Math.max(0, Math.min(100, score)));
}

// ── Weekly Room Utility Cost ──────────────────────────────────────────────────
export function calculateRoomUtilityCost(room) {
  // Lower baseline utility costs slightly to improve early-stage survival
  const baseByType = { greenhouse: 120, indoor: 150, industrial: 420 };
  const base = baseByType[room.type] || 180;
  // Lights on adds 40% to utility cost
  const lightMultiplier = room.environment?.lightOn ? 1.4 : 1.0;
  // CO2 system add-on
  const co2Addition = room.equipment?.co2sys?.installed ? 40 : 0;
  return Math.round(base * lightMultiplier + co2Addition);
}

// ── Total Weekly Burn Rate ────────────────────────────────────────────────────
export function calculateWeeklyBurn(state) {
  const wageCost = state.workers.reduce((s, w) => s + w.currentWage, 0);
  const utilityCost = state.rooms.filter(r => r.unlocked).reduce(
    (s, r) => s + calculateRoomUtilityCost(r), 0
  );
  const licenseRenewal = [0, 20, 48, 96][state.licenseLevel] || 0; // weekly proration of annual fee
  // Reduce nutrient cost per active room to ease early cash pressure
  const nutrientCost = state.rooms.filter(r => r.batch).length * 20; // ~$20/week per active room
  return wageCost + utilityCost + licenseRenewal + nutrientCost;
}

// ── Weekly Finance Processing ─────────────────────────────────────────────────
export function processWeeklyFinances(state) {
  const burn = calculateWeeklyBurn(state);
  const newCash = state.cash - burn;
  const newHistory = [
    ...state.cashHistory.slice(-51), // keep last 52 weeks
    { week: state.gameWeek, balance: newCash }
  ];
  return {
    cash: newCash,
    cashHistory: newHistory,
    totalExpenses: state.totalExpenses + burn,
    weeklyBurn: burn,
  };
}

// ── Market Price Fluctuation (weekly) ────────────────────────────────────────
export function updateMarketPrices(state) {
  const { marketPrices, gameWeek, supplyGlut } = state;
  // Seasonal demand: sine wave, peak at week 26 and 52
  const seasonal = 1 + Math.sin((gameWeek / 52) * Math.PI * 2) * 0.08;
  // Random weekly fluctuation ±5%
  const fluctuate = () => 0.95 + Math.random() * 0.10;
  const glutMod = supplyGlut ? 0.85 : 1.0;
  // Trend modifier
  const trendMods = { rising: 1.02, stable: 1.0, falling: 0.98 };
  const trend = trendMods[state.marketTrend] || 1.0;

  const newPrices = {
    wholesale:   Math.round(marketPrices.wholesale   * seasonal * fluctuate() * glutMod * trend * 100) / 100,
    medical:     Math.round(marketPrices.medical     * seasonal * fluctuate() * glutMod * trend * 100) / 100,
    retail:      Math.round(marketPrices.retail      * seasonal * fluctuate() * glutMod * trend * 100) / 100,
    blackmarket: Math.round(marketPrices.blackmarket * seasonal * fluctuate() * 1.0 * trend * 100) / 100, // black market less affected by glut
  };

  // Clamp prices to reasonable ranges (allow higher upside)
  newPrices.wholesale   = Math.max(1.50, Math.min(7.00,  newPrices.wholesale));
  newPrices.medical     = Math.max(2.50, Math.min(10.00, newPrices.medical));
  newPrices.retail      = Math.max(4.00, Math.min(16.00, newPrices.retail));
  newPrices.blackmarket = Math.max(9.00, Math.min(24.00, newPrices.blackmarket));

  // ── Extract price fluctuation ──────────────────────────────────────────────
  newPrices.extractRosin = Math.round(
    (marketPrices.extractRosin ?? 150) * seasonal * fluctuate() * glutMod * trend * 100
  ) / 100;
  newPrices.extractWax = Math.round(
    (marketPrices.extractWax ?? 30) * seasonal * fluctuate() * glutMod * trend * 100
  ) / 100;
  newPrices.extractShatter = Math.round(
    (marketPrices.extractShatter ?? 35) * seasonal * fluctuate() * glutMod * trend * 100
  ) / 100;

  // Clamp extract prices to ranges
  newPrices.extractRosin = Math.max(100, Math.min(200, newPrices.extractRosin));
  newPrices.extractWax = Math.max(20, Math.min(40, newPrices.extractWax));
  newPrices.extractShatter = Math.max(10, Math.min(60, newPrices.extractShatter));

  // Market trend evolves slowly
  let newTrend = state.marketTrend;
  if (Math.random() < 0.15) { // 15% chance each week trend changes
    newTrend = ['rising', 'stable', 'stable', 'falling'][Math.floor(Math.random() * 4)];
  }

  return {
    marketPrices: newPrices,
    marketTrend: newTrend,
    priceHistory: [
      ...state.priceHistory.slice(-51),
      { week: state.gameWeek, ...newPrices }
    ],
  };
}

// ── Contract Deadline Check ───────────────────────────────────────────────────
export function checkContractDeadlines(activeContracts, gameDay) {
  const updated = activeContracts.map(contract => {
    if (contract.status !== 'active') return contract;
    if (gameDay >= contract.deadline && contract.gramsDelivered < contract.gramsRequired) {
      return { ...contract, status: 'failed' };
    }
    return contract;
  });
  const failed = updated.filter(c => c.status === 'failed' && activeContracts.find(oc => oc.id === c.id)?.status === 'active');
  return { updated, failed };
}

// ── Stage Progression Check ───────────────────────────────────────────────────
export function checkStageProgression(state) {
  if (state.stage === 1) {
    if (state.totalRevenue >= 15000 || state.reputation >= 25) return 2;
  }
  if (state.stage === 2) {
    if ((state.reputation >= 50 && state.activeContracts.filter(c => c.status === 'fulfilled').length >= 5)
        || state.totalRevenue >= 100000) return 3;
  }
  if (state.stage === 3) {
    if (state.reputation >= 80 && state.brandValue >= 50) return 4;
  }
  return null;
}

// ── Reputation Gain from Sale ─────────────────────────────────────────────────
export function calculateRepGain(qualityScore, channel, unlockedTech) {
  const baseGain = qualityScore >= 80 ? 2 : qualityScore >= 60 ? 1 : 0.5;
  const channelMult = { wholesale: 0.8, medical: 1.2, retail: 1.5, blackmarket: -1 };
  const repBonus = unlockedTech?.includes('D2') ? 1.25 : 1.0;
  return Math.round(baseGain * (channelMult[channel] || 1) * repBonus * 10) / 10;
}

// ── License Purchase Cost ─────────────────────────────────────────────────────
export const LICENSE_COSTS = {
  1: 2500,   // Grower
  2: 5000,   // Processor
  3: 15000,  // Retailer
};

export const LICENSE_NAMES = {
  0: 'Unlicensed',
  1: 'Grower License',
  2: 'Processor License',
  3: 'Retailer License',
};

// ── Fine amounts ──────────────────────────────────────────────────────────────
export const VIOLATION_FINES = {
  minor:    { amount: 500,   repLoss: 2, label: 'Minor Violation' },
  moderate: { amount: 2000,  repLoss: 8, label: 'Compliance Failure' },
  major:    { amount: 8000,  repLoss: 20, label: 'Major Violation' },
  critical: { amount: 20000, repLoss: 50, label: 'License Suspension Risk' },
};
